import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bookmark, Share2 } from "lucide-react";
import { toast } from "react-toastify";
import { resolvePhotoUrl, surveyPhoto, handlePhotoError } from "../../utils/profilePhoto";
import { formatSurveyPostedDate, isSurveyExpired } from "../../utils/surveyPresentation";
import fallbackSurveyImage from "../../assets/img/Rectangle23.jpg";
import star from "../../assets/img/star.svg";
import { SurveyService } from "../../service/saveSurvey";
import { getSurveyForMe } from "../../service/surveyForMe";
import Swal from "sweetalert2";
import { removeSavedSurvey } from "../../service/mySurvey";

// Helper function to format end date
const formatEndDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const SavedSurveysPage = () => {
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchSavedSurveysList = async () => {
      try {
        setLoading(true);
        const [response, surveyMetaResponse] = await Promise.all([
          SurveyService.fetchSavedSurveys(),
          getSurveyForMe("saved").catch(() => null),
        ]);
        if (!isMounted) return;
        if (!response) return;

        if (response && response.data && response.data.success) {
          const rawData = response.data.data || [];
          const metadataById = new Map(
            (surveyMetaResponse?.data?.data || []).map((item) => [String(item.id), item])
          );
          const mapped = rawData.map((item) => {
            const metadata = metadataById.get(String(item.id)) || {};
            return {
              savedId: item.saved_id,
              id: item.id,
              author: {
                name: item.creator_name || metadata.creator_name || "Survey creator",
                jobTitle: item.job_title || metadata.job_title || "Product Researcher",
                avatar: surveyPhoto({ ...metadata, ...item }),
              },
              postedAt: item.survey_created_on || metadata.survey_created_on || item.created_on || metadata.created_on,
              coverImage: resolvePhotoUrl(item.survey_image || metadata.survey_image, fallbackSurveyImage),
              title: item.survey_title || "Untitled Survey",
              description: item.survey_description || "No description provided.",
              rewardCoins: parseFloat(item.reward_per_participant) || 0,
              tags: [item.sector, item.industry, item.country].filter(Boolean),
              endDate: item.end_date,
            };
          });
          setSurveys(mapped);
        } else {
          setSurveys([]);
        }
      } catch (error) {
        if (!isMounted) return;
        console.error("Error fetching saved surveys:", error);
        setSurveys([]);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchSavedSurveysList();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleRemoveSaved = async (surveyId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you really want to remove this survey from your saved list?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#c72430",
      cancelButtonColor: "#858C95",
      confirmButtonText: "Yes, remove it!",
      cancelButtonText: "Cancel",
      reverseButtons: true
    });

    if (!result.isConfirmed) return;

    if (removingId) return;
    try {
      setRemovingId(surveyId);
      const response = await removeSavedSurvey(surveyId);
      if (response && response.data && response.data.success) {
        setSurveys((prev) => prev.filter((s) => s.id !== surveyId));
        toast.success("Survey removed from saved list successfully!");
      } else {
        toast.error(response?.data?.message || "Failed to remove survey.");
      }
    } catch (error) {
      console.error("Error removing saved survey:", error);
      toast.error(
        error.response?.data?.message || "An error occurred while removing the survey."
      );
    } finally {
      setRemovingId(null);
    }
  };

  const handleStartSurvey = (item) => {
    if (isSurveyExpired(item.endDate)) {
      toast.error("This survey has expired.");
      return;
    }
    navigate(`/take-survey/${item.id}`, { state: { surveyMeta: item } });
  };

  if (loading) {
    return (
      <div className="dashboard-create-survey surveys-for-me-page">
        <div className="sfm-header d-flex justify-content-between align-items-center">
          <div>
            <h1>Saved Surveys</h1>
            <p>Loading saved surveys...</p>
          </div>
          <Link to="/survey-for-me" className="sfm-saved-btn">
            Back to Surveys For Me
          </Link>
        </div>
        <div className="sfm-list">
          <div className="sfm-loading">
            <div className="spinner"></div>
            <p>Loading saved surveys...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-create-survey surveys-for-me-page">
      <div className="sfm-header d-flex justify-content-between align-items-center">
        <div>
          <h1>Saved Surveys</h1>
          <p>
            {surveys.length} {surveys.length === 1 ? "Survey" : "Surveys"} Saved
          </p>
        </div>
        <Link to="/survey-for-me" className="sfm-saved-btn">
          Back to Surveys For Me
        </Link>
      </div>

      <div className="sfm-list">
        {surveys.length > 0 ? (
          surveys.map((item) => (
            <div className={`post-card mb-4 ${isSurveyExpired(item.endDate, new Date(now)) ? "expired" : ""}`} key={item.id} style={{ background: "#ffffff", border: "1px solid #ede7e1", borderRadius: "12px", padding: "14px", boxShadow: "0 6px 20px rgba(30, 24, 17, 0.04)" }}>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center">
                  <img src={item.author.avatar} onError={handlePhotoError} alt={item.author.name} className="post-user-img" style={{ width: "34px", height: "34px", borderRadius: "50%", objectFit: "cover" }} />
                  <div className="ms-3">
                    <h5 className="ft-13 fw-bold mb-1" style={{ color: "#2c2e33", margin: 0 }}>{item.author.name}</h5>
                    <p className="ft-11 mb-0" style={{ color: "#9d9ea5", fontWeight: 500 }}>
                      {item.author.jobTitle} {item.postedAt && <><span>-</span> {formatSurveyPostedDate(item.postedAt, now)}</>}
                    </p>
                  </div>
                </div>
                {isSurveyExpired(item.endDate, new Date(now)) && (
                  <div className="d-flex align-items-center gap-1 fw-bold ft-12" style={{ color: "#c72430" }}>
                    <i className="bi bi-info-circle" style={{ fontSize: "14px" }}></i>
                    <span>Expired On {formatEndDate(item.endDate)}</span>
                  </div>
                )}
              </div>

              <div className="position-relative mb-4" style={{ borderRadius: "8px", overflow: "hidden", background: "#f2f4f7" }}>
                <img src={item.coverImage} onError={(event) => { event.currentTarget.onerror = null; event.currentTarget.src = fallbackSurveyImage; }} alt={item.title} className="post-banner" style={{ width: "100%", height: "170px", display: "block", objectFit: "cover" }} />
                <div className="reward-badge ft-11 d-flex gap-2 align-items-center" style={{
                  position: "absolute",
                  top: "10px",
                  right: "10px",
                  minHeight: "32px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  border: "1px solid rgba(255, 255, 255, 0.29)",
                  borderRadius: "999px",
                  background: "rgba(255, 255, 255, 0.63)",
                  color: "#0E0E0D",
                  fontSize: "13px",
                  lineHeight: "18px",
                  fontWeight: "600",
                  padding: "7px 12px 7px 8px",
                  boxShadow: "0 8px 18px rgba(22, 25, 32, 0.12)",
                  backdropFilter: "blur(43.2px)",
                  WebkitBackdropFilter: "blur(43.2px)"
                }}>
                  <img src={star} alt="star" style={{ width: "18px", height: "18px" }} /> Reward: {item.rewardCoins} Coins
                </div>
              </div>

              <h2 className="post-title fw-bold mb-3 ft-20" style={{ fontSize: "15px", lineHeight: "21px", color: "#24282e", margin: "0 0 6px" }}>
                {item.title}
              </h2>

              <p className="post-desc ft-12 mb-3" style={{ color: "#858c95", fontSize: "12px", lineHeight: "18px", margin: 0 }}>
                {item.description}
              </p>

              <div className="d-flex flex-wrap gap-2 mb-4" style={{ marginTop: "10px" }}>
                {item.tags.map((tag, idx) => (
                  <span className="tag-box" key={idx} style={{
                    display: "inline-flex",
                    alignItems: "center",
                    minHeight: "22px",
                    border: "1px solid #ece8e2",
                    borderRadius: "6px",
                    background: "#ffffff",
                    color: "#6f747c",
                    fontSize: "11px",
                    lineHeight: "15px",
                    fontWeight: 500,
                    padding: "4px 8px"
                  }}>#{tag}</span>
                ))}
              </div>

              <div className="col-12 mt-16" style={{ borderTop: "1px solid #f0ece8", paddingTop: "12px", marginTop: "10px" }}>
                <div className="d-flex align-items-center justify-content-between">
                  <button
                    type="button"
                    className="btn border-0 rounded-3 d-flex align-items-center justify-content-center px-4 py-3"
                    style={{
                      background: "#FFF4F4",
                      color: "#c72430",
                      borderRadius: "6px",
                      transition: "all 0.2s ease"
                    }}
                    onClick={() => handleRemoveSaved(item.id)}
                    disabled={removingId === item.id}
                  >
                    <Bookmark size={15} fill="#c72430" color="#c72430" style={{ marginRight: "6px" }} />
                    <span className="ft-12 fw-bold">Remove Saved</span>
                  </button>

                  {isSurveyExpired(item.endDate, new Date(now)) ? (
                    <button
                      type="button"
                      className="w-33 fw-bold ft-14 text-white px-5 py-3 rounded-2 border-0"
                      disabled
                      style={{
                        minWidth: "118px",
                        minHeight: "40px",
                        borderRadius: "6px",
                        fontSize: "13px",
                        lineHeight: "18px",
                        backgroundColor: "#b4b4b4",
                        cursor: "not-allowed"
                      }}
                    >
                      Expired
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="w-33 fw-bold ft-14 text-white px-5 py-3 bgred rounded-2 cursor-target text-center hover:tw-bg-[#333] tw-transition-all tw-duration-300 tw-ease-in-out hover:tw-scale-105 border-0"
                      onClick={() => handleStartSurvey(item)}
                      style={{
                        minWidth: "118px",
                        minHeight: "40px",
                        borderRadius: "6px",
                        fontSize: "13px",
                        lineHeight: "18px"
                      }}
                    >
                      Start Survey
                    </button>
                  )}
                </div>
              </div>

              {isSurveyExpired(item.endDate, new Date(now)) && (
                <div className="col-12 mt-3" style={{
                  background: "#FFF4F4",
                  border: "1px solid #C72430",
                  borderRadius: "6px",
                  padding: "10px 14px",
                  display: "flex",
                  alignItems: "center",
                  color: "#2c2e33",
                  fontSize: "13px",
                  fontWeight: "500",
                  fontStyle: "italic"
                }}>
                  <i className="bi bi-info-circle me-2" style={{ color: "#C72430", fontSize: "16px", flexShrink: 0 }}></i>
                  <span>This survey is no longer accepting responses.</span>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="sfm-empty-state">
            <div className="sfm-empty-icon">🔖</div>
            <h3>No Saved Surveys</h3>
            <p>We couldn't find any saved surveys matching your profile.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SavedSurveysPage;
